import { useState, useCallback, useEffect } from 'react';

interface ToggleServiceOptions<T> {
  /** Initial state of the toggle */
  initialState: boolean;
  
  /** Function to call when toggle state changes */
  onToggle: (newState: boolean, data?: T) => Promise<void>;
  
  /** Function to call on successful toggle */
  onSuccess?: (newState: boolean, data?: T) => void;
  
  /** Function to call on toggle failure */
  onError?: (error: any, attemptedState: boolean, data?: T) => void;
  
  /** Optional data to pass to the callbacks */
  data?: T;
  
  /** Optional function to check initial state asynchronously */
  checkInitialState?: () => Promise<boolean>;
}

/**
 * Custom hook for managing toggle operations with service integration
 * Useful for like, bookmark, follow functionality
 */
export function useToggleService<T = any>({
  initialState,
  onToggle,
  onSuccess,
  onError,
  data,
  checkInitialState
}: ToggleServiceOptions<T>) {
  const [state, setState] = useState<boolean>(initialState);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  
  // Check initial state on mount if provided
  useEffect(() => {
    if (checkInitialState) {
      const fetchInitialState = async () => {
        try {
          const result = await checkInitialState();
          setState(result);
        } catch (err) {
          console.error('Error checking initial state:', err);
          // Keep the default initial state on error
        }
      };
      
      fetchInitialState();
    }
  }, [checkInitialState]);
  
  // Toggle function that calls the service
  const toggle = useCallback(async (e?: React.MouseEvent) => {
    // Prevent event propagation if event is provided
    if (e) {
      e.stopPropagation();
    }
    
    const newState = !state;
    setIsLoading(true);
    setError(null);
    
    try {
      await onToggle(newState, data);
      setState(newState);
      if (onSuccess) {
        onSuccess(newState, data);
      }
    } catch (err) {
      setError(err as Error);
      if (onError) {
        onError(err, newState, data);
      }
      console.error(`Error during toggle operation:`, err);
    } finally {
      setIsLoading(false);
    }
  }, [state, onToggle, onSuccess, onError, data]);
  
  // Set state directly (useful for initialization)
  const setStateDirectly = useCallback((newState: boolean) => {
    setState(newState);
  }, []);
  
  return {
    state,
    isLoading,
    error,
    toggle,
    setStateDirectly
  };
}

/**
 * Custom hook specifically for like functionality
 */
export function useLike(postId: string, initialLikeCount: number) {
  const [likeCount, setLikeCount] = useState(initialLikeCount);
  
  const { state: isLiked, toggle: toggleLike, isLoading, error } = useToggleService({
    initialState: false,
    onToggle: async (newState) => {
      const likeService = (await import('../lib/like-service')).likeService;
      if (newState) {
        await likeService.likeContent(postId);
      } else {
        await likeService.unlikeContent(postId);
      }
    },
    onSuccess: (newState) => {
      setLikeCount(prev => newState ? prev + 1 : Math.max(0, prev - 1));
    },
    checkInitialState: async () => {
      const likeService = (await import('../lib/like-service')).likeService;
      return likeService.hasLiked(postId);
    }
  });
  
  return { isLiked, toggleLike, likeCount, isLoading, error };
}

/**
 * Custom hook specifically for bookmark functionality
 */
export function useBookmark(postId: string, initialBookmarkCount: number) {
  const [bookmarkCount, setBookmarkCount] = useState(initialBookmarkCount);
  
  const { state: isBookmarked, toggle: toggleBookmark, isLoading, error } = useToggleService({
    initialState: false,
    onToggle: async (newState) => {
      const bookmarkService = (await import('../lib/supabase-service')).bookmarkService;
      if (newState) {
        await bookmarkService.bookmarkPost(postId);
      } else {
        await bookmarkService.unbookmarkPost(postId);
      }
    },
    onSuccess: (newState) => {
      setBookmarkCount(prev => newState ? prev + 1 : Math.max(0, prev - 1));
    },
    checkInitialState: async () => {
      const bookmarkService = (await import('../lib/supabase-service')).bookmarkService;
      return bookmarkService.hasBookmarked(postId);
    }
  });
  
  return { isBookmarked, toggleBookmark, bookmarkCount, isLoading, error };
}

/**
 * Custom hook specifically for follow functionality
 */
export function useFollow(userId: string, initialFollowersCount: number = 0) {
  const [followersCount, setFollowersCount] = useState(initialFollowersCount);
  
  const { state: isFollowing, toggle: toggleFollow, isLoading, error } = useToggleService({
    initialState: false, // Default to not following
    onToggle: async (newState) => {
      const followService = (await import('../lib/supabase-service')).followService;
      if (newState) {
        await followService.followUser(userId);
      } else {
        await followService.unfollowUser(userId);
      }
    },
    onSuccess: (newState) => {
      setFollowersCount(prev => newState ? prev + 1 : Math.max(0, prev - 1));
    },
    checkInitialState: async () => {
      const followService = (await import('../lib/supabase-service')).followService;
      return followService.isFollowing(userId);
    }
  });
  
  return { isFollowing, toggleFollow, followersCount, isLoading, error };
}
